// Shared, canonical invoice/session counts derived from the /invoices/balance-stats
// response. One definition, reused by Dashboard (Home), Balance, and Invoices so the
// same metric never differs across pages (see product docs 01 section 2 "Data
// consistency", and stories BAL-03 / INV-04).
//
// Decisions (2026-06):
//  - "Paid" = fully settled = fully-paid + overpaid. The backend `paid` bucket already
//    excludes overpaid (Invoice.js: paidInvoices = paid - overpaid), so the canonical
//    paidCount = paid + overpaid. "Partially paid" is a separate, non-overlapping bucket.
//  - Invoice counts shown on Dashboard/Balance are INVOICE-ONLY so they equal the
//    Invoices page exactly. Session and deposit figures are exposed separately and only
//    fold into combined revenue / combined counts.
//
// All buckets in /invoices/balance-stats are non-overlapping status counts, so summing
// is safe.

const settledCount = (bucket) => (bucket?.paid || 0) + (bucket?.overpaid || 0);

// Accepts the `data` object returned by GET /invoices/balance-stats.
export const deriveCounts = (balanceStats) => {
  const inv = balanceStats?.invoices || {};
  const ses = balanceStats?.sessions || {};
  const deposits = balanceStats?.standaloneDeposits?.depositCount || 0;

  return {
    invoice: {
      total: inv.total || 0,
      paidCount: settledCount(inv),        // fully paid + overpaid
      fullyPaidCount: inv.paid || 0,       // fully paid only (overpaid excluded)
      overpaidCount: inv.overpaid || 0,
      partiallyPaidCount: inv.partiallyPaid || 0,
      pendingCount: inv.pending || 0,
      overdueCount: inv.overdue || 0,
      // Funds arrived in a token the merchant does not accept, so the invoice is
      // still owed. Its own bucket, not part of paid.
      unsupportedCount: inv.unsupported || 0,
    },
    session: {
      total: ses.total || 0,
      paidCount: settledCount(ses),
      overpaidCount: ses.overpaid || 0,
      partiallyPaidCount: ses.partiallyPaid || 0,
      pendingCount: ses.pending || 0,
    },
    combined: {
      settledCount: settledCount(inv) + settledCount(ses) + deposits,
      pendingCount: (inv.pending || 0) + (ses.pending || 0),
      overpaidCount: (inv.overpaid || 0) + (ses.overpaid || 0),
    },
  };
};

export default { deriveCounts };
